import {
  CSSProperties,
  forwardRef,
  ForwardRefRenderFunction,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { HiXCircle } from "react-icons/hi";
import { Stage, useTransition } from "@vanyapr/transition-hook";
import { OSO_LIGHT_BLUE } from "../src/colors";
import FocusLock from "react-focus-lock";

/*
 * |-----------------------|
 * |         top           |
 * |-----------------------|
 * | left | coords | right |
 * |-----------------------|
 * |        bottom         |
 * |-----------------------|
 */
const DimBackground: React.FC<{ coords: DOMRect; stage: Stage }> = ({
  coords,
  stage,
}) => {
  const padding = 4;
  const bgClass = stage === "enter" ? "bg-opacity-40" : "bg-opacity-0";
  return ReactDOM.createPortal(
    <>
      <div // top
        className={`fixed bg-gray-600 z-40 inset-x-0 top-0 ${bgClass}`}
        style={{ height: coords.top - padding }}
      />
      <div // bottom
        className={`fixed bg-gray-600 z-40 inset-x-0 bottom-0 ${bgClass}`}
        style={{ top: coords.bottom + padding }}
      />
      <div // left
        className={`fixed bg-gray-600 z-40 left-0 ${bgClass}`}
        style={{
          top: coords.top - padding,
          height: coords.height + 2 * padding,
          width: coords.left - padding,
        }}
      />
      <div // right
        className={`fixed bg-gray-600 z-40 right-0 ${bgClass}`}
        style={{
          top: coords.top - padding,
          height: coords.height + 2 * padding,
          left: coords.right + padding,
        }}
      />
    </>,
    document.body
  );
};

function defaultDOMRect(): DOMRect {
  // document isn't defined when Next.js does SSR
  if (typeof document !== "undefined") {
    return document.body.getBoundingClientRect();
  } else {
    return {
      height: 0,
      width: 0,
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      x: 0,
      y: 0,
      toJSON: () => undefined,
    };
  }
}

const Coachmark: ForwardRefRenderFunction<
  HTMLDivElement,
  PropsWithChildren<{
    isOpen: boolean;
    target: RefObject<Element>;
    dismiss: () => void;
    focusLock?: string;
    left?: boolean;
    bottom?: boolean;
    listenForScroll?: RefObject<Element | null>[];
  }>
> = (
  {
    isOpen,
    target,
    dismiss,
    focusLock,
    left,
    bottom,
    children,
    listenForScroll,
  },
  ref
) => {
  const [coords, setCoords] = useState<DOMRect>(defaultDOMRect);
  const { stage, shouldMount } = useTransition(isOpen, 300);

  const updateCoords = useCallback(() => {
    const highlightNode = target.current;
    // TODO: fall back to a Modal when the target is not set
    if (!highlightNode) return;
    setCoords(highlightNode.getBoundingClientRect());
  }, [target]);

  useEffect(updateCoords, [updateCoords]);

  // hack to fix alignment when highlighting an opening modal
  useEffect(() => {
    const timeoutId = setTimeout(updateCoords, 300);
    return clearTimeout.bind(null, timeoutId);
  }, [target, stage, shouldMount, updateCoords]);

  useEffect(() => {
    window.addEventListener("resize", updateCoords);
    document.addEventListener("scroll", updateCoords);
    for (const element of listenForScroll ?? []) {
      element.current?.addEventListener("scroll", updateCoords);
    }
    return () => {
      window.removeEventListener("resize", updateCoords);
      document.removeEventListener("scroll", updateCoords);
      for (const element of listenForScroll ?? []) {
        element.current?.removeEventListener("scroll", updateCoords);
      }
    };
  });

  useEffect(() => {
    const escapeKeyListener = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", escapeKeyListener);
    return () => document.removeEventListener("keydown", escapeKeyListener);
  });

  if (!shouldMount) return null;

  const transformClasses = {
    from: "-transform-y-2 scale-95 opacity-0",
    enter: "",
    leave: "transform-y-2 scale-95 opacity-0",
  };

  const popoverStyle: CSSProperties = {
    top: coords.top,
    height: coords.height,
  };
  if (left) {
    popoverStyle.right = document.documentElement.clientWidth - coords.left;
    popoverStyle.maxWidth = coords.left;
  } else {
    popoverStyle.left = coords.right;
    popoverStyle.maxWidth = document.documentElement.clientWidth - coords.right;
  }
  const arrowClasses = left ? "border-l-8" : "border-r-8";
  const arrowStyle = {
    [left ? "borderLeftColor" : "borderRightColor"]: OSO_LIGHT_BLUE,
  };

  const popoverDialogY = bottom ? "-bottom-4" : "-top-4";
  const popoverDialogX = left ? "right-2" : "left-2";

  const content = (
    <div
      className={`transition fixed z-50 flex drop-shadow-lg ${
        left ? "flex-row-reverse" : "flex-row"
      } ${transformClasses[stage]}`}
      style={popoverStyle}
      ref={ref}
    >
      <div
        className={`h-0 w-0 border-y-8 border-y-transparent ${arrowClasses}`}
        style={arrowStyle}
      />
      <div
        className={`absolute ${popoverDialogX} ${popoverDialogY} p-4 bg-white rounded-lg h-fit w-96`}
        style={{ backgroundColor: OSO_LIGHT_BLUE }}
      >
        <button
          type="button"
          title="Close Walkthrough"
          onClick={dismiss}
          className="absolute top-2 right-2 text-xl transition-colors hover:text-red-500"
        >
          <HiXCircle />
        </button>
        {children}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <>
      {target.current && <DimBackground coords={coords} stage={stage} />}
      {focusLock !== undefined ? (
        <FocusLock group={focusLock}>{content}</FocusLock>
      ) : (
        content
      )}
    </>,
    document.body
  );
};

export default forwardRef(Coachmark);
